import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Settings, Store, CreditCard, Bell, Users, Globe, Shield,
  Save, RefreshCw, Mail, Phone, MapPin, Clock, Printer,
  Receipt, Wifi, Monitor, Smartphone, Key, QrCode, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';
import DisplaySettingsPage from './settings/DisplaySettingsPage';
import ImageUpload from '@/components/shared/ImageUpload';

export default function SettingsPage() {
  const context = useOutletContext() || {};
  const { token, manager, restaurant } = context;
  
  const [searchParams] = useSearchParams();
  // ?tab=display (linked from Payment Settings) opens that tab.
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'display' ? 'display' : 'business');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Settings states
  const [businessSettings, setBusinessSettings] = useState({
    name: '',
    slug: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    timezone: 'America/New_York'
  });
  
  const [operatingHours, setOperatingHours] = useState({
    monday: { open: '09:00', close: '21:00', closed: false },
    tuesday: { open: '09:00', close: '21:00', closed: false },
    wednesday: { open: '09:00', close: '21:00', closed: false },
    thursday: { open: '09:00', close: '21:00', closed: false },
    friday: { open: '09:00', close: '22:00', closed: false },
    saturday: { open: '10:00', close: '22:00', closed: false },
    sunday: { open: '10:00', close: '20:00', closed: false }
  });
  
  const [orderSettings, setOrderSettings] = useState({
    auto_accept_orders: false,
    min_order_amount: '0',
    delivery_fee: '5.00',
    delivery_radius: '5',
    enable_delivery: true,
    enable_pickup: true,
    enable_dine_in: true,
    prep_time_minutes: '15'
  });
  
  const [paymentSettings, setPaymentSettings] = useState({
    accept_cash: true,
    accept_card: true,
    accept_contactless: true,
    tip_enabled: true,
    default_tip_percentages: '15,18,20,25',
    surcharge_enabled: false,
    surcharge_percentage: '3'
  });
  
  const [notificationSettings, setNotificationSettings] = useState({
    new_order_sound: true,
    new_order_email: true,
    low_stock_alerts: true,
    daily_summary_email: false
  });
  
  // Security/2FA State
  const [securitySettings, setSecuritySettings] = useState({
    sms_2fa_enabled: true,
    totp_enabled: false
  });

  // Payment QR Codes State (Zelle, Venmo, Crypto)
  const [paymentQR, setPaymentQR] = useState({
    zelle_qr_url: '',
    venmo_qr_url: '',
    crypto_qr_url: '',
    zelle_label: '',
    venmo_label: '',
    crypto_label: ''
  });
  const [qrLoading, setQrLoading] = useState(false);

  const loadPaymentQR = useLatestCallback(async () => {
    try {
      const rid = manager?.restaurant_id || 'default';
      const res = await axios.get(`${API}/display/payment-qr/${rid}`);
      if (res.data?.config) {
        setPaymentQR(prev => ({ ...prev, ...res.data.config }));
      }
    } catch { /* ignore - first load */ }
  });

  const savePaymentQR = async () => {
    setQrLoading(true);
    try {
      const rid = manager?.restaurant_id || 'default';
      await axios.put(`${API}/display/payment-qr/${rid}`, paymentQR);
      toast.success('Payment QR codes saved');
    } catch {
      toast.error('Failed to save QR codes');
    } finally {
      setQrLoading(false);
    }
  };
  const [showTOTPSetup, setShowTOTPSetup] = useState(false);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQRCode, setTotpQRCode] = useState('');
  const [totpVerifyCode, setTotpVerifyCode] = useState('');
  const [settingUpTOTP, setSettingUpTOTP] = useState(false);

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    if (restaurant) {
      setBusinessSettings({
        name: restaurant.name || '',
        slug: restaurant.slug || '',
        description: restaurant.description || '',
        phone: restaurant.phone || '',
        email: restaurant.email || '',
        address: restaurant.address || '',
        city: restaurant.city || '',
        state: restaurant.state || '',
        zip: restaurant.zip_code || '',
        timezone: restaurant.timezone || 'America/New_York'
      });
    }
  }, [restaurant]);

  // Load payment QR codes
  useEffect(() => {
    if (manager?.restaurant_id) loadPaymentQR();
  }, [manager?.restaurant_id, loadPaymentQR]);

  const handleSaveBusinessSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/restaurant/${manager?.restaurant_id}`,
        businessSettings,
        getHeaders()
      );
      toast.success('Business settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOrderSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/orders/${manager?.restaurant_id}`,
        orderSettings,
        getHeaders()
      );
      toast.success('Order settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePaymentSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/payments/${manager?.restaurant_id}`,
        paymentSettings,
        getHeaders()
      );
      toast.success('Payment settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/settings/notifications/${manager?.restaurant_id}`,
        notificationSettings,
        getHeaders()
      );
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  // Security/2FA handlers
  const handleSetupTOTP = async () => {
    setSettingUpTOTP(true);
    try {
      const response = await axios.post(
        `${API}/otp/setup-totp`,
        { email: manager?.email },
        getHeaders()
      );
      setTotpSecret(response.data.secret);
      setTotpQRCode(response.data.qr_code);
      setShowTOTPSetup(true);
    } catch (error) {
      toast.error('Failed to setup Google Authenticator');
    } finally {
      setSettingUpTOTP(false);
    }
  };
  
  const handleVerifyAndEnableTOTP = async () => {
    if (totpVerifyCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setSettingUpTOTP(true);
    try {
      await axios.post(
        `${API}/otp/enable-totp`,
        { secret: totpSecret, totp_code: totpVerifyCode },
        getHeaders()
      );
      setSecuritySettings({ ...securitySettings, totp_enabled: true });
      setShowTOTPSetup(false);
      setTotpVerifyCode('');
      toast.success('Google Authenticator enabled successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setSettingUpTOTP(false);
    }
  };
  
  const handleDisableTOTP = async () => {
    try {
      await axios.post(`${API}/otp/disable-totp`, {}, getHeaders());
      setSecuritySettings({ ...securitySettings, totp_enabled: false });
      toast.success('Google Authenticator disabled');
    } catch (error) {
      toast.error('Failed to disable Google Authenticator');
    }
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <div className="space-y-4 md:space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 text-sm md:text-base">Manage your restaurant configuration</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 md:space-y-6">
        {/* Responsive Tab List - Horizontal scroll on mobile */}
        <TabsList className="bg-gray-100 w-full overflow-x-auto flex-nowrap justify-start p-1">
          <TabsTrigger value="business" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap">
            <Store className="h-3 w-3 md:h-4 md:w-4" />
            <span>Business</span>
          </TabsTrigger>
          <TabsTrigger value="hours" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap">
            <Clock className="h-3 w-3 md:h-4 md:w-4" />
            <span>Hours</span>
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap">
            <Receipt className="h-3 w-3 md:h-4 md:w-4" />
            <span>Orders</span>
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap">
            <CreditCard className="h-3 w-3 md:h-4 md:w-4" />
            <span>Payments</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap">
            <Bell className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden sm:inline">Notifications</span>
            <span className="sm:hidden">Alerts</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap">
            <Shield className="h-3 w-3 md:h-4 md:w-4" />
            <span>Security</span>
          </TabsTrigger>
          <TabsTrigger value="display" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm whitespace-nowrap">
            <Monitor className="h-3 w-3 md:h-4 md:w-4" />
            <span>Display</span>
          </TabsTrigger>
        </TabsList>

        {/* Business Settings */}
        <TabsContent value="business">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="p-4 md:p-6">
              <CardTitle className="text-base md:text-lg">Business Information</CardTitle>
              <CardDescription className="text-xs md:text-sm">Update your restaurant&apos;s basic information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6 pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div className="md:col-span-2">
                  <Label className="text-sm">Restaurant Name</Label>
                  <Input
                    value={businessSettings.name}
                    onChange={(e) => setBusinessSettings({ ...businessSettings, name: e.target.value })}
                    placeholder="My Restaurant"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="text-sm">Business Slug (POS Device ID)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="business-slug-input"
                      value={businessSettings.slug}
                      onChange={(e) => setBusinessSettings({ ...businessSettings, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                      placeholder="my-restaurant"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    POS devices use this slug to connect to your restaurant. Only lowercase letters, numbers, and hyphens.
                  </p>
                </div>
                
                <div className="md:col-span-2">
                  <Label className="text-sm">Description</Label>
                  <Textarea
                    value={businessSettings.description}
                    onChange={(e) => setBusinessSettings({ ...businessSettings, description: e.target.value })}
                    placeholder="Tell customers about your restaurant..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label className="text-sm">Phone Number</Label>
                  <Input
                    value={businessSettings.phone}
                    onChange={(e) => setBusinessSettings({ ...businessSettings, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={businessSettings.email}
                    onChange={(e) => setBusinessSettings({ ...businessSettings, email: e.target.value })}
                    placeholder="contact@restaurant.com"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label>Street Address</Label>
                  <Input
                    value={businessSettings.address}
                    onChange={(e) => setBusinessSettings({ ...businessSettings, address: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                
                <div>
                  <Label>City</Label>
                  <Input
                    value={businessSettings.city}
                    onChange={(e) => setBusinessSettings({ ...businessSettings, city: e.target.value })}
                    placeholder="New York"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>State</Label>
                    <Input
                      value={businessSettings.state}
                      onChange={(e) => setBusinessSettings({ ...businessSettings, state: e.target.value })}
                      placeholder="NY"
                    />
                  </div>
                  <div>
                    <Label>ZIP Code</Label>
                    <Input
                      value={businessSettings.zip}
                      onChange={(e) => setBusinessSettings({ ...businessSettings, zip: e.target.value })}
                      placeholder="10001"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Timezone</Label>
                  <Select
                    value={businessSettings.timezone}
                    onValueChange={(val) => setBusinessSettings({ ...businessSettings, timezone: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/New_York">Eastern Time</SelectItem>
                      <SelectItem value="America/Chicago">Central Time</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={handleSaveBusinessSettings} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operating Hours */}
        <TabsContent value="hours">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Operating Hours</CardTitle>
              <CardDescription>Set your restaurant&apos;s opening and closing times</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {days.map((day) => (
                <div key={day} className="flex items-center gap-4 py-3 border-b last:border-0">
                  <div className="w-28">
                    <span className="font-medium capitalize">{day}</span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!operatingHours[day].closed}
                      onCheckedChange={(checked) => setOperatingHours({
                        ...operatingHours,
                        [day]: { ...operatingHours[day], closed: !checked }
                      })}
                    />
                    <span className="text-sm text-slate-500">
                      {operatingHours[day].closed ? 'Closed' : 'Open'}
                    </span>
                  </div>
                  
                  {!operatingHours[day].closed && (
                    <>
                      <Input
                        type="time"
                        value={operatingHours[day].open}
                        onChange={(e) => setOperatingHours({
                          ...operatingHours,
                          [day]: { ...operatingHours[day], open: e.target.value }
                        })}
                        className="w-32"
                      />
                      <span className="text-slate-400">to</span>
                      <Input
                        type="time"
                        value={operatingHours[day].close}
                        onChange={(e) => setOperatingHours({
                          ...operatingHours,
                          [day]: { ...operatingHours[day], close: e.target.value }
                        })}
                        className="w-32"
                      />
                    </>
                  )}
                </div>
              ))}
              
              <div className="flex justify-end pt-4">
                <Button onClick={() => toast.success('Hours saved')} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Hours
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Order Settings */}
        <TabsContent value="orders">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Order Settings</CardTitle>
              <CardDescription>Configure how you receive and process orders</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Auto-Accept Orders</p>
                    <p className="text-sm text-slate-500">Automatically confirm incoming orders</p>
                  </div>
                  <Switch
                    checked={orderSettings.auto_accept_orders}
                    onCheckedChange={(checked) => setOrderSettings({ ...orderSettings, auto_accept_orders: checked })}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Delivery</p>
                    </div>
                    <Switch
                      checked={orderSettings.enable_delivery}
                      onCheckedChange={(checked) => setOrderSettings({ ...orderSettings, enable_delivery: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Pickup</p>
                    </div>
                    <Switch
                      checked={orderSettings.enable_pickup}
                      onCheckedChange={(checked) => setOrderSettings({ ...orderSettings, enable_pickup: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Dine-in</p>
                    </div>
                    <Switch
                      checked={orderSettings.enable_dine_in}
                      onCheckedChange={(checked) => setOrderSettings({ ...orderSettings, enable_dine_in: checked })}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Minimum Order Amount ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={orderSettings.min_order_amount}
                      onChange={(e) => setOrderSettings({ ...orderSettings, min_order_amount: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label>Default Prep Time (minutes)</Label>
                    <Input
                      type="number"
                      value={orderSettings.prep_time_minutes}
                      onChange={(e) => setOrderSettings({ ...orderSettings, prep_time_minutes: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label>Delivery Fee ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={orderSettings.delivery_fee}
                      onChange={(e) => setOrderSettings({ ...orderSettings, delivery_fee: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label>Delivery Radius (miles)</Label>
                    <Input
                      type="number"
                      value={orderSettings.delivery_radius}
                      onChange={(e) => setOrderSettings({ ...orderSettings, delivery_radius: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={handleSaveOrderSettings} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Order Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings */}
        <TabsContent value="payments">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Payment Settings</CardTitle>
              <CardDescription>Configure payment methods and options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold">Accepted Payment Methods</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">💵</span>
                      <span className="font-medium">Cash</span>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_cash}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, accept_cash: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">💳</span>
                      <span className="font-medium">Credit/Debit</span>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_card}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, accept_card: checked })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">📱</span>
                      <span className="font-medium">Contactless</span>
                    </div>
                    <Switch
                      checked={paymentSettings.accept_contactless}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, accept_contactless: checked })}
                    />
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-4">Tipping</h3>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-4">
                    <div>
                      <p className="font-medium">Enable Tips</p>
                      <p className="text-sm text-slate-500">Allow customers to add tips</p>
                    </div>
                    <Switch
                      checked={paymentSettings.tip_enabled}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, tip_enabled: checked })}
                    />
                  </div>
                  
                  {paymentSettings.tip_enabled && (
                    <div>
                      <Label>Default Tip Options (%)</Label>
                      <Input
                        value={paymentSettings.default_tip_percentages}
                        onChange={(e) => setPaymentSettings({ ...paymentSettings, default_tip_percentages: e.target.value })}
                        placeholder="15,18,20,25"
                      />
                      <p className="text-xs text-slate-500 mt-1">Comma-separated values</p>
                    </div>
                  )}
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-4">Card Processing Surcharge</h3>
                  
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-4">
                    <div>
                      <p className="font-medium">Enable Surcharge</p>
                      <p className="text-sm text-slate-500">Add a fee for card payments</p>
                    </div>
                    <Switch
                      checked={paymentSettings.surcharge_enabled}
                      onCheckedChange={(checked) => setPaymentSettings({ ...paymentSettings, surcharge_enabled: checked })}
                    />
                  </div>
                  
                  {paymentSettings.surcharge_enabled && (
                    <div>
                      <Label>Surcharge Percentage (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={paymentSettings.surcharge_percentage}
                        onChange={(e) => setPaymentSettings({ ...paymentSettings, surcharge_percentage: e.target.value })}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Payment QR Codes Section */}
              <div className="pt-4 border-t">
                <h3 className="font-semibold mb-2">Payment QR Codes</h3>
                <p className="text-sm text-slate-500 mb-4">Upload QR codes for Zelle, Venmo, and Crypto. These will be shown to customers at checkout.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Zelle */}
                  <div className="p-4 bg-violet-50 rounded-lg space-y-3" data-testid="zelle-qr-section">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="h-4 w-4 text-violet-600" />
                      <span className="font-medium text-violet-800">Zelle</span>
                    </div>
                    <ImageUpload
                      value={paymentQR.zelle_qr_url}
                      onChange={(url) => setPaymentQR(prev => ({ ...prev, zelle_qr_url: url }))}
                      folder="payment-qr"
                      label="QR Code Image"
                    />
                    <Input
                      placeholder="e.g. pay@myrestaurant.com"
                      value={paymentQR.zelle_label || ''}
                      onChange={(e) => setPaymentQR(prev => ({ ...prev, zelle_label: e.target.value }))}
                      className="text-sm"
                    />
                  </div>

                  {/* Venmo */}
                  <div className="p-4 bg-cyan-50 rounded-lg space-y-3" data-testid="venmo-qr-section">
                    <div className="flex items-center gap-2 mb-1">
                      <Smartphone className="h-4 w-4 text-cyan-600" />
                      <span className="font-medium text-cyan-800">Venmo</span>
                    </div>
                    <ImageUpload
                      value={paymentQR.venmo_qr_url}
                      onChange={(url) => setPaymentQR(prev => ({ ...prev, venmo_qr_url: url }))}
                      folder="payment-qr"
                      label="QR Code Image"
                    />
                    <Input
                      placeholder="e.g. @MyRestaurant"
                      value={paymentQR.venmo_label || ''}
                      onChange={(e) => setPaymentQR(prev => ({ ...prev, venmo_label: e.target.value }))}
                      className="text-sm"
                    />
                  </div>

                  {/* Crypto */}
                  <div className="p-4 bg-orange-50 rounded-lg space-y-3" data-testid="crypto-qr-section">
                    <div className="flex items-center gap-2 mb-1">
                      <QrCode className="h-4 w-4 text-orange-600" />
                      <span className="font-medium text-orange-800">Crypto</span>
                    </div>
                    <ImageUpload
                      value={paymentQR.crypto_qr_url}
                      onChange={(url) => setPaymentQR(prev => ({ ...prev, crypto_qr_url: url }))}
                      folder="payment-qr"
                      label="QR Code Image"
                    />
                    <Input
                      placeholder="e.g. BTC wallet label"
                      value={paymentQR.crypto_label || ''}
                      onChange={(e) => setPaymentQR(prev => ({ ...prev, crypto_label: e.target.value }))}
                      className="text-sm"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end mt-4">
                  <Button onClick={savePaymentQR} disabled={qrLoading} variant="outline" data-testid="save-payment-qr-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {qrLoading ? 'Saving...' : 'Save QR Codes'}
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={handleSavePaymentSettings} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Payment Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
              <CardDescription>Configure how you receive alerts and updates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">New Order Sound</p>
                  <p className="text-sm text-slate-500">Play a sound when new orders arrive</p>
                </div>
                <Switch
                  checked={notificationSettings.new_order_sound}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, new_order_sound: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">New Order Email</p>
                  <p className="text-sm text-slate-500">Receive email notifications for new orders</p>
                </div>
                <Switch
                  checked={notificationSettings.new_order_email}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, new_order_email: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Low Stock Alerts</p>
                  <p className="text-sm text-slate-500">Get notified when inventory is running low</p>
                </div>
                <Switch
                  checked={notificationSettings.low_stock_alerts}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, low_stock_alerts: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium">Daily Summary Email</p>
                  <p className="text-sm text-slate-500">Receive daily business summary via email</p>
                </div>
                <Switch
                  checked={notificationSettings.daily_summary_email}
                  onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, daily_summary_email: checked })}
                />
              </div>
              
              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveNotificationSettings} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Notification Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Security Settings */}
        <TabsContent value="security">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security & Two-Factor Authentication
              </CardTitle>
              <CardDescription>Protect your account with additional security measures</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SMS 2FA */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Smartphone className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">SMS Verification</p>
                      <p className="text-sm text-slate-500">Receive a code via text message when logging in</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check className="h-3 w-3 mr-1" />
                    Active
                  </Badge>
                </div>
                <p className="text-xs text-slate-500 ml-13">
                  SMS verification is enabled by default for all accounts. Codes are sent to your registered phone number.
                </p>
              </div>
              
              {/* Google Authenticator */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Key className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Google Authenticator</p>
                      <p className="text-sm text-slate-500">Use an authenticator app for time-based codes</p>
                    </div>
                  </div>
                  {securitySettings.totp_enabled ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <Check className="h-3 w-3 mr-1" />
                      Enabled
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600">
                      Not Set Up
                    </Badge>
                  )}
                </div>
                
                <div className="flex gap-2 ml-13">
                  {securitySettings.totp_enabled ? (
                    <Button variant="outline" size="sm" onClick={handleDisableTOTP} className="text-red-600 hover:text-red-700">
                      Disable
                    </Button>
                  ) : (
                    <Button size="sm" onClick={handleSetupTOTP} disabled={settingUpTOTP}>
                      <QrCode className="h-4 w-4 mr-2" />
                      {settingUpTOTP ? 'Setting up...' : 'Set Up Now'}
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Password Change Section */}
              <div className="p-4 border border-slate-200 rounded-lg space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Change Password
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Current Password</Label>
                    <Input type="password" placeholder="Enter current password" />
                  </div>
                  <div></div>
                  <div>
                    <Label>New Password</Label>
                    <Input type="password" placeholder="Enter new password" />
                  </div>
                  <div>
                    <Label>Confirm New Password</Label>
                    <Input type="password" placeholder="Confirm new password" />
                  </div>
                </div>
                <Button variant="outline">
                  Update Password
                </Button>
              </div>
              
              {/* Session Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 text-blue-800">
                  <Shield className="h-4 w-4" />
                  <span className="text-sm font-medium">Your account is protected</span>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  Two-factor authentication adds an extra layer of security to your account.
                  Even if someone knows your password, they&apos;ll need access to your phone to log in.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Display Settings */}
        <TabsContent value="display">
      
      {/* TOTP Setup Dialog */}
          <DisplaySettingsPage />
        </TabsContent>
      </Tabs>
      <Dialog open={showTOTPSetup} onOpenChange={setShowTOTPSetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Set Up Google Authenticator
            </DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app, then enter the verification code.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* QR Code */}
            <div className="flex justify-center">
              {totpQRCode && (
                <img 
                  src={totpQRCode} 
                  alt="TOTP QR Code" 
                  className="w-48 h-48 border rounded-lg"
                />
              )}
            </div>
            
            {/* Manual Entry Key */}
            <div className="text-center">
              <p className="text-sm text-slate-500 mb-2">Or enter this code manually:</p>
              <code className="bg-slate-100 px-3 py-2 rounded text-sm font-mono select-all">
                {totpSecret}
              </code>
            </div>
            
            {/* Verification Code Input */}
            <div className="space-y-3">
              <Label className="text-center block">Enter verification code from app:</Label>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={totpVerifyCode}
                  onChange={setTotpVerifyCode}
                  onComplete={handleVerifyAndEnableTOTP}
                  autoFocus
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="w-10 h-12 font-semibold" />
                    <InputOTPSlot index={1} className="w-10 h-12 font-semibold" />
                    <InputOTPSlot index={2} className="w-10 h-12 font-semibold" />
                    <InputOTPSlot index={3} className="w-10 h-12 font-semibold" />
                    <InputOTPSlot index={4} className="w-10 h-12 font-semibold" />
                    <InputOTPSlot index={5} className="w-10 h-12 font-semibold" />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            
            <Button 
              onClick={handleVerifyAndEnableTOTP} 
              disabled={settingUpTOTP || totpVerifyCode.length !== 6}
              className="w-full"
            >
              {settingUpTOTP ? 'Verifying...' : 'Verify & Enable'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
